import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CollabDocStorage } from '@common/collab/enums/collab-doc-storage.enum';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabCommitMergeResult, CollabCommitResult } from '@common/collab/models/collab-commit-result.model';
import { CollabDashboardData } from '@common/collab/models/collab-dashboard-data.model';
import { CollabDocCommitRequestBody } from '@common/collab/models/collab-doc-commit-request-body.model';
import { CollabDocContentRequestBody } from '@common/collab/models/collab-doc-content-request-body.model';
import { CollabDocCreateRequestBody } from '@common/collab/models/collab-doc-create-request-body.model';
import { CollabDocDeleteRequestBody } from '@common/collab/models/collab-doc-delete-request-body.model';
import { CollabDocErrorItem } from '@common/collab/models/collab-doc-error-item.model';
import { CollabDocFailedSync } from '@common/collab/models/collab-doc-failed-sync.model';
import { CollabDocMongoItem } from '@common/collab/models/collab-doc-mongo-item.model';
import { CollabDocPullOrFetchRequestBody } from '@common/collab/models/collab-doc-pull-or-fetch-request-body.model';
import { CollabDocPullRequestBody } from '@common/collab/models/collab-doc-pull-request-body.model';
import { CollabDocRedisItem } from '@common/collab/models/collab-doc-redis-item.model';
import { CollabDocRedisJSON } from '@common/collab/models/collab-doc-redis-json.model';
import { CollabDocRenameRequestBody } from '@common/collab/models/collab-doc-rename-request-body.model';
import { CollabDocRestoreVersionRequestBody } from '@common/collab/models/collab-doc-restore-version-request-body.model';
import { CollabDocStepChunkRequestBody } from '@common/collab/models/collab-doc-step-chunk-request-body.model';
import { CollabDocStepChunk } from '@common/collab/models/collab-doc-step-chunk.model';
import { CollabMetaDataMongoItem } from '@common/collab/models/collab-meta-data-mongo-item.model';
import { ProjectFileParameters, ReviewFileParameters } from '@common/collab/types/doc-parameters.type';
import { DomCompareResult } from '@common/html/types/html-compare.type';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { StepJSON } from '@common/prosemirror/transform/json';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFailedSync } from '@common/workspace/models/workspace-failed-sync.model';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { WorkspacePullRequestBody } from '@common/workspace/models/workspace-pull-request-body.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Base64 } from 'js-base64';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CollabApiService {
  constructor(private http: HttpClient, private apiService: ApiService) { }

  @SharePendingRequest()
  getCollabHealth$(): Observable<any> {
    return this.http.get<any>(`${this.apiService.collabApiUrl}/api/health`);
  }

  @SharePendingRequest()
  getDashboardData$(): Observable<CollabDashboardData> {
    return this.http.get<CollabDashboardData>(`${this.apiService.collabApiUrl}/api/dashboard`);
  }

  @SharePendingRequest()
  getCollabDocMongoItemsPage$(filter: PageFilter): Observable<Page<CollabDocMongoItem>> {
    return this.http.post<Page<CollabDocMongoItem>>(`${this.apiService.collabApiUrl}/api/docs/mongo`, filter);
  }

  @SharePendingRequest()
  getCollabDocRedisItemsPage$(filter: PageFilter): Observable<Page<CollabDocRedisItem>> {
    return this.http.post<Page<CollabDocRedisItem>>(`${this.apiService.collabApiUrl}/api/docs/redis`, filter);
  }

  @SharePendingRequest()
  getCollabDocErrorItemsPage$(filter: PageFilter): Observable<Page<CollabDocErrorItem>> {
    return this.http.post<Page<CollabDocErrorItem>>(`${this.apiService.collabApiUrl}/api/docs/errors`, filter);
  }

  @SharePendingRequest()
  getDocFailedSyncPage$(filter: PageFilter): Observable<Page<CollabDocFailedSync>> {
    return this.http.post<Page<CollabDocFailedSync>>(`${this.apiService.collabApiUrl}/api/docs/failed-syncs`, filter);
  }

  @SharePendingRequest()
  getWorkspaceFailedSyncPage$(filter: PageFilter): Observable<Page<WorkspaceFailedSync>> {
    return this.http.post<Page<WorkspaceFailedSync>>(`${this.apiService.collabApiUrl}/api/workspaces/failed-syncs`, filter);
  }

  getMongoDocCode$(docId: string): Observable<string> {
    return this.http.get(`${this.apiService.collabApiUrl}/api/docs/${docId}/code/mongo`, {
      responseType: 'text'
    }).pipe(
      map(code => {
        return Base64.decode(code);
      })
    );
  }

  @SharePendingRequest()
  getRedisDocCode$(docId: string, schemaName: string): Observable<string> {
    return this.http.get(`${this.apiService.collabApiUrl}/api/docs/${docId}/code/redis/${schemaName}`, {
      responseType: 'text'
    }).pipe(
      map(code => {
        return Base64.decode(code);
      })
    );
  }

  @SharePendingRequest()
  getRedisDocByDocId$(docId: string, schemaName: string): Observable<CollabDocRedisItem> {
    return this.http.get<CollabDocRedisItem>(`${this.apiService.collabApiUrl}/api/docs/${docId}/redis/${schemaName}`);
  }

  @SharePendingRequest()
  getMongoDoc$(docId: string): Observable<CollabDocMongoItem> {
    return this.http.get<CollabDocMongoItem>(`${this.apiService.collabApiUrl}/api/docs/${docId}/mongo`);
  }

  @SharePendingRequest()
  getMetaDataPage$(filter: PageFilter): Observable<Page<CollabMetaDataMongoItem>> {
    return this.http.post<Page<CollabMetaDataMongoItem>>(`${this.apiService.collabApiUrl}/api/metadata`, filter);
  }

  @SharePendingRequest()
  removeDocFromStorage$(docId: string, storage: CollabDocStorage, schemaName: string): Observable<void> {
    return this.http.delete<void>(`${this.apiService.collabApiUrl}/api/docs/${docId}/${storage}/${schemaName}`);
  }

  @SharePendingRequest()
  roundtrip$(code: string, schemaName: string): Observable<MadCloudResult<DomCompareResult>> {
    return this.http.post<MadCloudResult<DomCompareResult>>(`${this.apiService.collabApiUrl}/api/docs/roundtrip/${schemaName}`, {
      code: Base64.encode(code)
    });
  }

  @SharePendingRequest()
  getCollabDoc$(fileType: CollabFileType, fileParams: ProjectFileParameters | ReviewFileParameters): Observable<CollabDocRedisJSON> {
    return this.http.post<CollabDocRedisJSON>(`${this.apiService.collabAuthorityUrl}/api/docs`, { fileType, ...fileParams });
  }

  @SharePendingRequest()
  getWorkspaceFile$(projectId: number, branchName: string, filePath: string): Observable<WorkspaceFile> {
    return this.http.post<WorkspaceFile>(`${this.apiService.collabApiUrl}/api/workspaces/workspaceFile`, { projectId, branchName, filePath });
  }

  bulkUpdateWorkspaceFileStatus$(projectId: number, docIds: string[], schemaNames: string[], userId: string, status: WorkspaceFileUserStatus, docVersions?: number[]): Observable<any> {
    return this.http.post<any>(`${this.apiService.collabAuthorityUrl}/api/workspaces/updateWorkspaceFileUserStatus`, {
      docIds,
      docVersions,
      projectId,
      schemaNames,
      status,
      userId
    });
  }

  @SharePendingRequest()
  getWorkspaceFilesPage$(filter: PageFilter, projectId: number, branchName: string): Observable<any> {
    return this.http.post<any>(`${this.apiService.collabApiUrl}/api/workspaces/${projectId}/${encodeURIComponent(branchName)}/workspaceFilesPage`, { filter });
  }

  getCollabDocByDocId$(docId: string, schemaName: string): Observable<CollabDocRedisJSON> {
    return this.http.get<CollabDocRedisJSON>(`${this.apiService.collabAuthorityUrl}/api/docs/doc/${docId}/${schemaName}`);
  }

  @SharePendingRequest()
  getCollabDocStepChunk$(body: CollabDocStepChunkRequestBody): Observable<CollabDocStepChunk> {
    return this.http.post<CollabDocStepChunk>(`${this.apiService.collabAuthorityUrl}/api/docs/stepChunk`, body);
  }


  @SharePendingRequest()
  getCollabDocContent$(body: CollabDocContentRequestBody): Observable<string> {
    return this.http.post<string>(`${this.apiService.collabAuthorityUrl}/api/docs/content`, body, {
      responseType: 'text' as 'json'
    }).pipe(
      map(content => {
        return Base64.decode(content);
      })
    );
  }

  commitCollabDocs$(body: CollabDocCommitRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/commit`, body);
  }

  createCollabDoc$(body: CollabDocCreateRequestBody): Observable<void> {
    return this.http.post<void>(`${this.apiService.collabAuthorityUrl}/api/docs/create`, body);
  }

  deleteCollabDoc$(body: CollabDocDeleteRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/delete`, body);
  }

  renameCollabDoc$(body: CollabDocRenameRequestBody): Observable<CollabCommitResult> {
    return this.http.post<CollabCommitResult>(`${this.apiService.collabAuthorityUrl}/api/docs/rename`, body);
  }

  pullCollabDoc$(body: CollabDocPullRequestBody): Observable<CollabCommitMergeResult> {
    return this.http.post<CollabCommitMergeResult>(`${this.apiService.collabAuthorityUrl}/api/docs/pull`, body);
  }

  pullOrFetchCollabDocs$(body: CollabDocPullOrFetchRequestBody): Observable<CollabCommitMergeResult> {
    return this.http.post<CollabCommitMergeResult>(`${this.apiService.collabAuthorityUrl}/api/docs/pullOrFetch`, body);
  }

  @SharePendingRequest()
  fetchWorkspace$(projectId: number, branchName: string): Observable<MadCloudResult> {
    return this.http.post<MadCloudResult>(`${this.apiService.collabAuthorityUrl}/api/workspaces/fetch`, { projectId, branchName });
  }

  pullWorkspace$(body: WorkspacePullRequestBody): Observable<CollabCommitMergeResult> {
    return this.http.post<CollabCommitMergeResult>(`${this.apiService.collabAuthorityUrl}/api/workspaces/pull`, body);
  }

  restoreCollabDocToVersion$(body: CollabDocRestoreVersionRequestBody): Observable<{ docId: string, invertedSteps: StepJSON[], steps: StepJSON[] }> {
    return this.http.post<{ docId: string, invertedSteps: StepJSON[], steps: StepJSON[] }>(`${this.apiService.collabAuthorityUrl}/api/docs/restore`, body);
  }
}
